// components/onboarding/OnboardingReminderBanner.jsx
//
// App-wide, non-intrusive reminder shown on authenticated pages while onboarding is incomplete.
// Renders nothing once business ops are unlocked, or while loading/unavailable. Dismissible for the
// session, except for CHANGE_REQUESTED / REJECTED / pending-agreement (which stay visible).
// See docs/onboarding-analysis/ONBOARDING_UX_REDESIGN.md §5.

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useOnboardingProgress from '../../hooks/useOnboardingProgress';

export default function OnboardingReminderBanner() {
  const navigate = useNavigate();
  const { progress, businessOpsUnlocked, completionPercent } = useOnboardingProgress();
  const [dismissed, setDismissed] = useState(false);

  // Only show on a definitive "locked" signal - never while loading/unauthenticated/unlocked.
  if (!progress || businessOpsUnlocked !== false) return null;

  const status = progress.status;
  // Once submitted, the account can place orders while review is in progress — no nagging banner.
  if (status === 'PENDING_VERIFICATION') return null;
  const agreementPending = progress.agreementRequired && !progress.agreementSigned;
  const isRejected = status === 'REJECTED';
  const isChangeRequested = status === 'CHANGE_REQUESTED';
  const nonDismissible = isRejected || isChangeRequested || agreementPending;

  if (dismissed && !nonDismissible) return null;

  let tone = 'bg-amber-50 border-amber-200 text-amber-900';
  let accent = 'from-amber-400 to-orange-500';
  let message;
  let cta = 'Continue onboarding';

  if (agreementPending) {
    message = 'Action needed - please review and sign your merchant agreement to activate your account.';
    cta = 'Review & sign';
  } else if (isRejected) {
    tone = 'bg-red-50 border-red-200 text-red-900';
    accent = 'from-red-400 to-rose-500';
    message = 'Your application was not approved. Contact support or view details.';
    cta = 'View details';
  } else if (isChangeRequested) {
    tone = 'bg-orange-50 border-orange-200 text-orange-900';
    accent = 'from-orange-400 to-red-500';
    message = 'Changes were requested on your application. Review and re-submit to continue.';
    cta = 'Review changes';
  } else if (status === 'PENDING_VERIFICATION') {
    tone = 'bg-blue-50 border-blue-200 text-blue-900';
    accent = 'from-blue-400 to-indigo-500';
    message = "Your account is under review - we'll notify you once it's approved.";
    cta = 'View status';
  } else {
    message = `Finish onboarding to start shipping - ${completionPercent}% complete.`;
  }

  return (
    <div className={`sticky top-0 z-10 border-b ${tone}`} role="status" aria-live="polite">
      <div className="max-w-[1920px] mx-auto px-4 py-2.5 flex items-center gap-3">
        <span className="text-lg shrink-0" aria-hidden="true">🚀</span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{message}</p>
          {!isRejected && status !== 'PENDING_VERIFICATION' && (
            <div className="mt-1 h-1.5 w-full max-w-xs bg-white/60 rounded-full overflow-hidden">
              <div className={`h-full bg-gradient-to-r ${accent} transition-all duration-500`} style={{ width: `${Math.max(0, Math.min(100, completionPercent))}%` }} />
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => navigate('/onboarding')}
          className="shrink-0 inline-flex items-center px-3.5 py-1.5 rounded-lg bg-white/80 hover:bg-white text-sm font-semibold shadow-sm transition-colors"
        >
          {cta} →
        </button>
      </div>
    </div>
  );
}
